
import * as THREE from 'three';
import { StairElement, LevelElement, StairRun, StairLanding } from './types';
import { mergeGeometriesIntoMesh } from '../rendering/instancing/mergeGeometries';

export class StairSolver {

    /**
     * Calculate Riser Count and Actual Height based on Levels.
     */
    static solveStair(
        stair: StairElement,
        baseLevel: LevelElement,
        topLevel: LevelElement
    ): void {
        const h1 = baseLevel.geometryData?.elevation || 0;
        const h2 = topLevel.geometryData?.elevation || 3.0; // Assume 3m if missing

        const totalHeight = Math.abs(h2 - h1);

        const desiredRiser = stair.geometryData.desiredRiserHeight || 0.18; // Default 180mm

        // Count = Total / Desired
        const count = Math.round(totalHeight / desiredRiser);
        const actualCount = count < 1 ? 1 : count;

        const actualRiser = totalHeight / actualCount;

        // Apply Results
        stair.geometryData.riserCount = actualCount;
        stair.geometryData.actualRiserHeight = actualRiser;
        stair.geometryData.actualTreadDepth = stair.geometryData.desiredTreadDepth || 0.28;
    }

    /**
     * Calculate required run length for a straight run.
     */
    static calculateRunLength(riserCount: number, treadDepth: number): number {
        if (riserCount <= 1) return 0;
        return (riserCount - 1) * treadDepth;
    }

    /**
     * Validate Stair Rules
     * @returns Array of warning strings
     */
    static validate(stair: StairElement): string[] {
        const warnings: string[] = [];
        // Max Riser Check (e.g. 190mm)
        if (stair.geometryData.actualRiserHeight > 0.190) {
            warnings.push(`Riser height ${stair.geometryData.actualRiserHeight.toFixed(3)}m exceeds maximum (0.190m).`);
        }
        return warnings;
    }

    /**
     * Generate 3D Mesh for the entire Stair assembly.
     */
    static createStairMesh(stair: StairElement): THREE.Mesh {
        const data = stair.geometryData;
        // Group mesh for all components? 
        // Three.js doesn't support grouping in a single Mesh unless we merge geometries.
        // We will merge geometries.

        const geometries: THREE.BufferGeometry[] = [];

        // 1. Runs
        if (data.runs) {
            data.runs.forEach(run => {
                const runGeo = this.createRunGeometry(run, data.actualRiserHeight, data.actualTreadDepth);
                geometries.push(runGeo);
            });
        }

        // 2. Landings
        if (data.landings) {
            data.landings.forEach(landing => {
                const landingGeo = this.createLandingGeometry(landing);
                geometries.push(landingGeo);
            });
        }

        if (geometries.length === 0) return new THREE.Mesh();

        // Merge via BufferGeometryUtils (task 3.2: resolves former TODO merge)
        return mergeGeometriesIntoMesh(geometries);
    }

    private static createRunGeometry(run: StairRun, riserHeight: number, treadDepth: number): THREE.BufferGeometry {
        // Simplified Straight Run:
        // Assume path is 2 points: Start -> End
        if (run.path.length < 2) return new THREE.BufferGeometry();

        const p1 = new THREE.Vector3(...run.path[0]);
        const p2 = new THREE.Vector3(...run.path[run.path.length - 1]);

        // Direction vector
        const dir = p2.clone().sub(p1).normalize();
        // Right vector
        const up = new THREE.Vector3(0, 0, 1);
        const right = dir.clone().cross(up).normalize();

        // Width offsets
        const halfWidth = run.width / 2;

        const vertices: number[] = [];
        const indices: number[] = [];

        const currentPos = p1.clone();
        // Start elevation
        currentPos.z = run.baseElevation;

        // Steps
        // For each tread, we create a box? Or standard "ZigZag"?
        // Let's create a Solid Step (Filled to bottom? Or Stringer style?)
        // Assume Monolithic for simplicity: Fill to some depth or just thin plates? 
        // BIM default is monolithic often. Let's do simple steps.

        for (let i = 0; i < run.riserCount; i++) {
            // Bottom Left, Bottom Right, Top Left, Top Right of the RISER FACE
            // Then Tread FACE.

            // Step Origin (Base of Riser)
            const stepBase = currentPos.clone().add(dir.clone().multiplyScalar(i * treadDepth));
            stepBase.z = run.baseElevation + (i * riserHeight);

            // Vertices for this step

            // 1. Riser Face
            // BL = stepBase - right*halfWidth
            // BR = stepBase + right*halfWidth
            // TL = BL + up*riserHeight
            // TR = BR + up*riserHeight

            const BL = stepBase.clone().sub(right.clone().multiplyScalar(halfWidth));
            const BR = stepBase.clone().add(right.clone().multiplyScalar(halfWidth));
            const TL = BL.clone(); TL.z += riserHeight;
            const TR = BR.clone(); TR.z += riserHeight;

            // Tread Face
            // Tread extends 'treadDepth' along 'dir' from Top of Riser

            const TreadBL = TL.clone();
            const TreadBR = TR.clone();
            const TreadTL = TreadBL.clone().add(dir.clone().multiplyScalar(treadDepth));
            const TreadTR = TreadBR.clone().add(dir.clone().multiplyScalar(treadDepth));

            // Add Quads
            this.addQuad(vertices, indices, BL, BR, TR, TL); // Riser
            this.addQuad(vertices, indices, TreadBL, TreadBR, TreadTR, TreadTL); // Tread

            // Sides (Optional for solid look)
            // Left Side: BL -> TL -> TreadTL -> (Base+Depth?)
            // For simplicity, just Riser/Tread surfaces for now.
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geo.setIndex(indices);
        geo.computeVertexNormals();
        return geo;
    }

    private static createLandingGeometry(landing: StairLanding): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        if (landing.boundary.length > 0) {
            shape.moveTo(landing.boundary[0][0], landing.boundary[0][1]);
            for (let i = 1; i < landing.boundary.length; i++) shape.lineTo(landing.boundary[i][0], landing.boundary[i][1]);
            shape.closePath();
        }

        const geo = new THREE.ExtrudeGeometry(shape, { depth: landing.thickness, bevelEnabled: false });
        // Extrude z is depth.
        // Landing elevation is Top surface.
        // Translate to elevation - thickness
        geo.translate(0, 0, landing.elevation - landing.thickness);
        return geo;
    }

    private static addQuad(vertices: number[], indices: number[], v1: THREE.Vector3, v2: THREE.Vector3, v3: THREE.Vector3, v4: THREE.Vector3) {
        const idx = vertices.length / 3;
        vertices.push(v1.x, v1.y, v1.z);
        vertices.push(v2.x, v2.y, v2.z);
        vertices.push(v3.x, v3.y, v3.z);
        vertices.push(v4.x, v4.y, v4.z);

        // CCW: 1, 2, 4; 2, 3, 4
        indices.push(idx, idx + 1, idx + 3);
        indices.push(idx + 1, idx + 2, idx + 3);
    }

}

